import React, { useMemo } from "react";
import classNames from "classnames";
import { IEvidence, ProgramData, ProgramSkill } from "@interfaces/Interfaces";
import * as EvidenceUtils from "@utils/evidenceUtils";
import * as ProgramUtils from "@utils/programUtils";
import { isSameDay, subMonths, isAfter } from "date-fns";

type Props = {
  sortedSkillsForRole: ProgramSkill[];
  programEvidence: IEvidence[];
  program: ProgramData;
  collapse?: boolean;
};

const ProgramSkillsRAGCard: React.FC<Props> = (props) => {
  const evidenceWithSkills = useMemo(
    () => props.programEvidence.filter((evidence) => EvidenceUtils.evidenceHasSkills(evidence, props.program.ID)),
    [props.programEvidence]
  );

  const greenCount = () => {
    const requiredSkills = props.sortedSkillsForRole.filter(
      (skill) => parseInt(skill.numberRequiredInDuration ?? 0, 10) > 0
    );

    let count = 0;

    for (const skill of requiredSkills) {
      const evidenceForSkill = evidenceWithSkills.filter((evidence) => {
        const evidenceJSON = evidence.evidenceJSON;
        const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

        if (onHoldReset === 1 || evidence.draft) {
          return false;
        }

        const evidenceSkillKey = ProgramUtils.getSkillEvidenceKey(props.program);

        if (evidenceSkillKey) {
          const evidenceSkill = evidenceJSON[evidenceSkillKey];

          return evidenceSkill?.trim() === skill.Name.trim();
        }

        return false;
      });

      const duration = parseInt(skill.duration ?? 0, 10);

      const dateThreshold = duration > 0 ? subMonths(new Date(), duration - 1).setDate(1) : undefined;

      const evidenceForSkillWithinDateThreshold = dateThreshold
        ? evidenceForSkill.filter(
            (evidence) =>
              isSameDay(new Date(evidence.date), dateThreshold) || isAfter(new Date(evidence.date), dateThreshold)
          )
        : evidenceForSkill;

      const numberRequiredInDuration = parseInt(skill.numberRequiredInDuration ?? 0, 10);

      if (evidenceForSkillWithinDateThreshold.length >= numberRequiredInDuration) {
        count++;
      }
    }

    return count;
  };

  const redCount = () => {
    const requiredSkills = props.sortedSkillsForRole.filter(
      (skill) => parseInt(skill.numberRequiredInDuration ?? 0, 10) > 0
    );

    let count = 0;

    for (const skill of requiredSkills) {
      const evidenceForSkill = evidenceWithSkills.filter((evidence) => {
        const evidenceJSON = evidence.evidenceJSON;
        const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

        if (onHoldReset === 1 || evidence.draft) {
          return false;
        }

        const evidenceSkillKey = ProgramUtils.getSkillEvidenceKey(props.program);

        if (evidenceSkillKey) {
          const evidenceSkill = evidenceJSON[evidenceSkillKey];

          return evidenceSkill?.trim() === skill.Name.trim();
        }

        return false;
      });

      const duration = parseInt(skill.duration ?? 0, 10);

      const dateThreshold = duration > 0 ? subMonths(new Date(), duration - 1).setDate(1) : undefined;

      const evidenceForSkillWithinDateThreshold = dateThreshold
        ? evidenceForSkill.filter(
            (evidence) =>
              isSameDay(new Date(evidence.date), dateThreshold) || isAfter(new Date(evidence.date), dateThreshold)
          )
        : evidenceForSkill;

      if (evidenceForSkillWithinDateThreshold.length === 0) {
        count++;
      }
    }

    return count;
  };

  const amberCount = () => {
    const requiredSkills = props.sortedSkillsForRole.filter(
      (skill) => parseInt(skill.numberRequiredInDuration ?? 0, 10) > 0
    );

    let count = 0;

    for (const skill of requiredSkills) {
      const evidenceForSkill = evidenceWithSkills.filter((evidence) => {
        const evidenceJSON = evidence.evidenceJSON;
        const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

        if (onHoldReset === 1 || evidence.draft) {
          return false;
        }

        const evidenceSkillKey = ProgramUtils.getSkillEvidenceKey(props.program);

        if (evidenceSkillKey) {
          const evidenceSkill = evidenceJSON[evidenceSkillKey];

          return evidenceSkill?.trim() === skill.Name.trim();
        }

        return false;
      });

      const duration = parseInt(skill.duration ?? 0, 10);

      const dateThreshold = duration > 0 ? subMonths(new Date(), duration - 1).setDate(1) : undefined;

      const evidenceForSkillWithinDateThreshold = dateThreshold
        ? evidenceForSkill.filter(
            (evidence) =>
              isSameDay(new Date(evidence.date), dateThreshold) || isAfter(new Date(evidence.date), dateThreshold)
          )
        : evidenceForSkill;

      const numberRequiredInDuration = parseInt(skill.numberRequiredInDuration ?? 0, 10);

      if (
        evidenceForSkillWithinDateThreshold.length > 0 &&
        evidenceForSkillWithinDateThreshold.length < numberRequiredInDuration
      ) {
        count++;
      }
    }

    return count;
  };

  // const totalCount = () => {
  //   // TODO: update this to be the total number of skills
  //   return 11;
  // };

  return (
    <div
      className={classNames("flex flex-col mb-4", {
        "mb-0": props.collapse,
      })}
    >
      <div className="flex flex-col">
        {/* <div className="flex flex-row items-end">
          <div className="text-acc-34px font-acc-700 tracking-[-0.5] leading-none text-green-light-text">
            {totalCount()}
          </div>
          <div className="text-acc-15px font-acc-700 tracking-default leading-none text-grey-90 mb-1">
            &nbsp;{"total"}
          </div>
        </div> */}
        <div className="flex flex-col mt-2">
          <div className="flex flex-row items-center justify-between h-11">
            <div>{"Compliant"}</div>
            <div className="flex items-center justify-center size-5 rounded-full bg-green-background">
              <div className="text-12px font-semibold text-green-text">{`${greenCount()}`}</div>
            </div>
          </div>
          <div className="h-[1px] bg-grey-30 w-full" />
        </div>
        <div className="flex flex-col">
          <div className="flex flex-row items-center justify-between h-11">
            <div>{"Warning"}</div>
            <div className="flex items-center justify-center size-5 rounded-full bg-orange-20">
              <div className="text-12px font-semibold text-orange-text">{`${amberCount()}`}</div>
            </div>
          </div>
          <div className="h-[1px] bg-grey-30 w-full" />
        </div>
        <div className="flex flex-col">
          <div className="flex flex-row items-center justify-between h-11">
            <div>{"Attention required"}</div>
            <div className="flex items-center justify-center size-5 rounded-full bg-cta-red">
              <div className="text-12px font-semibold text-white">{`${redCount()}`}</div>
            </div>
          </div>
          <div className="h-[1px] bg-grey-30 w-full" />
        </div>
      </div>
    </div>
  );
};

export default ProgramSkillsRAGCard;
